import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import shark from '../media/icons/shark.mp4';
import static_shark from '../media/icons/static_shark.jpg';
import desert from '../media/icons/desert.JPG';
// import pumpkin from '../media/icons/pumpkin.jpg';
// import pumpkin_demo from '../media/icons/pumpkin_demo.mp4';

const lifeAspects = [
  {
    id: 1,
    title: "Max",
    backgroundImage: desert,
    backgroundVideo: null,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    primaryAction: {
      label: "View My Work",
      url: "#professional"
    },
  },
  // {
  //   id: 2,
  //   title: "The Journey",
  //   subtitle: "Where it all started",
  //   backgroundImage: pumpkin,
  //   backgroundVideo: pumpkin_demo,
  //   gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  //   primaryAction: {
  //     label: "Read More",
  //     url: "/about/started",
  //     isModal: true
  //   },
  // },
  {
    id: 3,
    title: "Beyond The Code",
    backgroundImage: static_shark,
    backgroundVideo: shark,
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    primaryAction: {
      label: "Learn More",
      url: "#about"
    },
  }
];

const Hero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const currentAspect = lifeAspects[currentIndex];

  const handleDotClick = (index) => {
    if (index === currentIndex) return;
    setIsAnimating(false);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(true);
    }, 300);
  };

  const handlePrevious = () => {
    const newIndex = currentIndex === 0 ? lifeAspects.length - 1 : currentIndex - 1;
    handleDotClick(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex === lifeAspects.length - 1 ? 0 : currentIndex + 1;
    handleDotClick(newIndex);
  };

  // Handle Video Reset on Slide Change
  useEffect(() => {
    setIsVideoPlaying(false);
    setIsMuted(true); // Always reset to muted for new slide
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }

    // Mobile Autoplay Logic (Netflix Style)
    // If on mobile (width < 1024px) or touch device, try to autoplay muted after slide transition
    const isMobile = window.innerWidth < 1024 || window.matchMedia('(hover: none)').matches;

    if (isMobile && currentAspect.backgroundVideo) {
      const mobilePlayTimeout = setTimeout(() => {
        if (videoRef.current) {
          setIsVideoPlaying(true);
          videoRef.current.play().catch(err => {
            console.log('Mobile autoplay failed:', err);
            setIsVideoPlaying(false);
          });
        }
      }, 1000); // Wait 1s for slide animation to finish
      return () => clearTimeout(mobilePlayTimeout);
    }
  }, [currentIndex, currentAspect.backgroundVideo]);

  const handleMouseEnter = () => {
    // Desktop Hover Logic (Only for mouse devices wide enough)
    const isDesktop = window.innerWidth >= 1024 && window.matchMedia('(hover: hover)').matches;

    if (isDesktop && currentAspect.backgroundVideo) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsVideoPlaying(true);
        if (videoRef.current) {
          videoRef.current.play().catch(err => console.log('Video play failed:', err));
        }
      }, 500);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Only pause on desktop hover leave. On mobile, we keep playing until slide change.
    const isDesktop = window.innerWidth >= 1024 && window.matchMedia('(hover: hover)').matches;

    if (isDesktop) {
      setIsVideoPlaying(false);
      setIsMuted(true);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation(); // Prevent triggering parent click events
    setIsMuted(!isMuted);
  };

  const handleHeroClick = () => {
    // Mobile Tap to Toggle Logic
    const isMobile = window.innerWidth < 1024 || window.matchMedia('(hover: none)').matches;

    if (isMobile && currentAspect.backgroundVideo && videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      } else {
        videoRef.current.play().catch(err => console.log('Tap play failed:', err));
        setIsVideoPlaying(true);
      }
    }
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation(); // Prevent hero click
    if (action.isModal) {
      e.preventDefault();
      navigate(action.url, { state: { background: location } });
    } else if (action.url.startsWith('#')) {
      e.preventDefault();
      const targetId = action.url.substring(1);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  return (
    <div
      className="relative h-[85vh] min-h-[550px] w-full overflow-hidden cursor-pointer md:cursor-default"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleHeroClick}
    >
      <div
        className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
      >
        {currentAspect.backgroundVideo ? (
          <>
            <div
              className={`absolute inset-0 w-full h-full transition-opacity duration-700 ${isVideoPlaying ? 'opacity-0' : 'opacity-100'
                }`}
              style={{
                background: currentAspect.backgroundImage ? `url(${currentAspect.backgroundImage}) center/cover` : currentAspect.gradient,
              }}
            />
            <video
              ref={videoRef}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isVideoPlaying ? 'opacity-100' : 'opacity-0'
                }`}
              loop
              muted={isMuted}
              playsInline
              preload="none"
              poster={currentAspect.backgroundImage}
              key={currentAspect.id}
            >
              <source src={currentAspect.backgroundVideo} type="video/mp4" />
            </video>

            {/* Mute/Unmute Button (Visible when video plays) */}
            {isVideoPlaying && (
              <button
                onClick={toggleMute}
                className="absolute top-24 right-6 z-30 p-2 bg-black/50 backdrop-blur-md rounded-full text-white hover:bg-black/70 transition-all border border-white/20"
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                )}
              </button>
            )}
          </>
        ) : (
          <div className="absolute inset-0 w-full h-full animate-ken-burns"
            style={{
              background: currentAspect.backgroundImage ? `url(${currentAspect.backgroundImage}) center/cover` : currentAspect.gradient,
            }}
          />
        )}
      </div>

      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />

      <div className="absolute bottom-0 left-0 p-8 md:p-12 lg:p-16 w-full md:w-1/2 lg:w-2/5 pb-20 md:pb-24">
        <div className="space-y-4">
          <h1
            className="text-5xl md:text-6xl lg:text-8xl font-bold leading-none tracking-tight text-white"
            style={{
              animation: isAnimating ? 'slide-up 0.6s ease-out 0.2s forwards' : 'none',
              opacity: 0
            }}
          >
            {currentAspect.title}
          </h1>

          {currentAspect.subtitle && (
            <div
              className="flex flex-col gap-1"
              style={{
                animation: isAnimating ? 'fade-in 0.6s ease-out 0.25s forwards' : 'none',
                opacity: 0
              }}
            >
              <p className="text-2xl md:text-3xl font-semibold text-white">
                {currentAspect.subtitle}
              </p>
            </div>
          )}

          <div
            className="flex flex-wrap gap-3 pt-4"
            style={{
              animation: isAnimating ? 'slide-left 0.6s ease-out 0.5s forwards' : 'none',
              opacity: 0
            }}
          >
            <a
              href={currentAspect.primaryAction.url}
              onClick={(e) => handleActionClick(e, currentAspect.primaryAction)}
              className="flex items-center gap-2 bg-white text-black px-8 py-3 rounded-md font-semibold hover:bg-white/80 transition-all duration-200 shadow-lg cursor-pointer"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
              {currentAspect.primaryAction.label}
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-2 z-10">
        {lifeAspects.map((aspect, index) => (
          <button
            key={aspect.id}
            onClick={(e) => { e.stopPropagation(); handleDotClick(index); }} // Stop propagation
            className={`transition-all duration-300 ${index === currentIndex
              ? 'w-12 h-2 bg-white'
              : 'w-2 h-2 bg-white/50 hover:bg-white/70'
              } rounded-full`}
            aria-label={`Go to life aspect ${index + 1}`}
          />
        ))}
      </div>

      {/* Left Arrow */}
      <button
        onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 group"
        aria-label="Previous slide"
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </div>
      </button>

      {/* Right Arrow */}
      <button
        onClick={(e) => { e.stopPropagation(); handleNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 group"
        aria-label="Next slide"
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
    </div>
  );
};

export default Hero;
